<script setup>
import DashboardActionsCell from "@frontJs/components/tables/cells/DashboardActionsCell.vue";
import DateRangePicker from "@js/components/DateRangePicker.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import ValuesCell from "@js/components/tables/cells/ValuesCell.vue";
import { useDataTable } from "@js/utils/dataTable";
import { hasRole } from "@js/utils/permission";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { type } = defineProps({
  type: {
    type: String,
    required: false,
  },
});

const { route } = useRoute();

const totalData = ref([]);

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  isTenantTable: true,
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("all.label.name"),
      cell: (props) =>
        hasRole("super_admin")
          ? h(LinkCellV2, {
              text: props.row.original.name,
              href: route("front.projectManagement.customer.updateCustomer", {
                id: props.row.original.id,
              }),
            })
          : props.row.original.url
            ? h(LinkCellV2, {
                text: props.row.original.name,
                href: props.row.original.url,
              })
            : h(TextCellV2, {
                text: props.row.original.name,
              }),
    }),
    columnHelper.accessor(() => null, {
      id: "turnoverOfToday",
      header: () => t("all.label.turnoverOfToday"),
      cell: (props) =>
        h(ValuesCell, {
          data: props.row.original,
          keyValue1: "turnoverOfToday",
          keyValue2: "turnoverOfYesterday",
          type: "price",
        }),
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "turnoverOfToday",
          keyValue2: "turnoverOfYesterday",
          type: "price",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "turnover",
      header: () => t("all.label.turnover"),
      cell: (props) =>
        h(ValuesCell, {
          data: props.row.original,
          keyValue1: "turnover1",
          keyValue2: "turnover2",
          color: true,
          type: "price",
        }),
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "turnover1",
          keyValue2: "turnover2",
          type: "price",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "seaCost",
      header: () => t("all.label.seaCost"),
      cell: (props) =>
        props.row.original.googleAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "seaCost1",
              keyValue2: "seaCost2",
              type: "price",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "seaCost1",
          keyValue2: "seaCost2",
          type: "price",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "turnoverPercentBySeaCost",
      header: () => t("all.label.turnoverPercentBySeaCost"),
      cell: (props) =>
        props.row.original.googleAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "turnoverPercentBySeaCost1",
              keyValue2: "turnoverPercentBySeaCost2",
              color: false,
              type: "percentage",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "turnoverPercentBySeaCost1",
          keyValue2: "turnoverPercentBySeaCost2",
          color: false,
          type: "percentage",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "seaReturnOnAdSpend",
      header: () => t("all.label.seaReturnOnAdSpend"),
      cell: (props) =>
        props.row.original.googleAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "seaReturnOnAdSpend1",
              keyValue2: "seaReturnOnAdSpend2",
              color: true,
              type: "number",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "seaReturnOnAdSpend1",
          keyValue2: "seaReturnOnAdSpend2",
          color: true,
          type: "number",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "smaCost",
      header: () => t("all.label.smaCost"),
      cell: (props) =>
        props.row.original.facebookAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "smaCost1",
              keyValue2: "smaCost2",
              type: "price",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "smaCost1",
          keyValue2: "smaCost2",
          type: "price",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "turnoverPercentBySmaCost",
      header: () => t("all.label.turnoverPercentBySmaCost"),
      cell: (props) =>
        props.row.original.facebookAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "turnoverPercentBySmaCost1",
              keyValue2: "turnoverPercentBySmaCost2",
              color: false,
              type: "percentage",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "turnoverPercentBySmaCost1",
          keyValue2: "turnoverPercentBySmaCost2",
          color: false,
          type: "percentage",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "smaReturnOnAdSpend",
      header: () => t("all.label.smaReturnOnAdSpend"),
      cell: (props) =>
        props.row.original.facebookAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "smaReturnOnAdSpend1",
              keyValue2: "smaReturnOnAdSpend2",
              color: true,
              type: "number",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "smaReturnOnAdSpend1",
          keyValue2: "smaReturnOnAdSpend2",
          color: true,
          type: "number",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "turnoverPercentBySeaAndSmaCosts",
      header: () => t("all.label.turnoverPercentBySeaAndSmaCosts"),
      cell: (props) =>
        props.row.original.googleAdsActive ||
        props.row.original.facebookAdsActive
          ? h(ValuesCell, {
              data: props.row.original,
              keyValue1: "turnoverPercentBySeaAndSmaCosts1",
              keyValue2: "turnoverPercentBySeaAndSmaCosts2",
              compareKeyValue2: "roasTargetPercentage",
              color: true,
              reverseColor: true,
              type: "percentage",
            })
          : null,
      footer: () =>
        h(ValuesCell, {
          data: totalData.value,
          keyValue1: "turnoverPercentBySeaAndSmaCosts1",
          keyValue2: "turnoverPercentBySeaAndSmaCosts2",
          color: true,
          reverseColor: true,
          type: "percentage",
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("all.label.actions"),
      headerClasses: "justify-center",
      cell: (props) =>
        h(DashboardActionsCell, {
          row: props.row,
        }),
    }),
  ],
  updateData,
  columnVisibility: {
    turnoverOfToday: type === "e-commerce" || type === undefined,
    turnover: type === "e-commerce" || type === undefined,
    turnoverPercentBySeaCost: type === "e-commerce" || type === undefined,
    turnoverPercentBySmaCost: type === "e-commerce" || type === undefined,
    turnoverPercentBySeaAndSmaCosts: type === "e-commerce" || type === undefined,
  },
});

function updateData() {
  loading.value = true;

  let url = route("front.postDashboard");

  let postData = {
    pageIndex: pagination.value.pageIndex,
    pageSize: pagination.value.pageSize,
    sorting: sorting.value,
    globalFilter: globalFilter.value,
    filters: filters.value,
    data: {
      type: type,
    },
  };

  let config = {};

  axios
    .post(url, postData, config)
    .then((response) => {
      data.value = response.data.data;
      totalData.value = response.data.totalData;
      count.value = response.data.count;
      pagination.value.pageIndex = response.data.pageIndex;
      pagination.value.pageSize = response.data.pageSize;
      pageCount.value = response.data.pageCount;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
    with-footer
    with-row-number
  >
    <template #actions>
      <DateRangePicker :callback="updateData" compare />
    </template>
  </DataTableLayout>
</template>
